import { TreeItem } from '@mui/lab';
import { useCallback } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { NodeData } from 'reaflow';
import useInLightMode from '../../hooks/useInLightMode';

interface Props {
  nodeId: string;
  label: string;
  fontColor: string;
  linkNodes: (from: NodeData<any>, to: NodeData<any>) => void;
  children?: any;
}

const DraggableTreeItem = ({
  nodeId,
  label,
  fontColor,
  linkNodes,
  children,
}: Props) => {
  const inLightMode = useInLightMode();
  const splitNode = nodeId.split('-');
  const itemType = splitNode[0];
  const [, drag] = useDrag(() => ({
    type: itemType,
    item: { name: nodeId, type: itemType },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));
  // Disable TreeView focus system to make elements draggable
  const dragRef = useCallback(
    (element: Element) => {
      element?.addEventListener('focusin', (e) => {
        e.stopImmediatePropagation();
      });
      drag(element);
    },
    [drag]
  );
  const onDrop = (item: { name: string }, monitor: DropTargetMonitor) => {
    if (!monitor.didDrop()) linkNodes({ id: nodeId }, { id: item.name });
  };
  const [, drop] = useDrop({
    accept: ['satellite', 'analyzer', 'switch', 'dish', 'location'],
    drop: onDrop,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });
  return (
    <div ref={drop}>
      <TreeItem
        nodeId={nodeId}
        label={label}
        sx={{
          color: inLightMode ? '#000' : fontColor,
          textDecoration: inLightMode ? 'underline' : undefined,
          textDecorationColor: fontColor,
          textDecorationThickness: '.2rem',
        }}
        ref={dragRef}
      >
        {children}
      </TreeItem>
    </div>
  );
};

export default DraggableTreeItem;
